(() => {
  const CORE_SELECTOR = 'script[data-k-search-core]';
  const finderAssetUrl = () => document.querySelector(CORE_SELECTOR)?.dataset?.finderUrl || '';

  const resolveAsset = (filename) => {
    const src = String(filename || '').trim();
    if (!src) return '';
    if (/^(?:https?:)?\/\//.test(src) || src.startsWith('/')) return src;
    try {
      const base = finderAssetUrl()
        ? new URL(finderAssetUrl(), window.location.href)
        : new URL(window.location.href);
      return new URL(src, base).href;
    } catch (_error) {
      return src;
    }
  };

  const waitForFinder = async () => {
    for (let attempt = 0; attempt < 30; attempt += 1) {
      if (window.KrepiteSearch?.loadFinderData) {
        try {
          return await window.KrepiteSearch.loadFinderData();
        } catch (_error) {
          return null;
        }
      }
      await new Promise((resolve) => window.setTimeout(resolve, 50));
    }
    return null;
  };

  const candidateIndex = (data) => {
    const index = new Map();
    Object.entries(data?.models || {}).forEach(([modelHandle, model]) => {
      (model?.candidate_parts || []).forEach((candidate) => {
        const enriched = {
          ...candidate,
          _modelHandle: modelHandle,
          _modelLabel: model?.model_label || modelHandle,
        };
        [
          candidate.product_handle,
          candidate.canonical_handle,
          String(candidate.product_preview?.url || '').split('/products/')[1] || '',
        ].filter(Boolean).forEach((key) => {
          if (!index.has(key)) index.set(key, []);
          index.get(key).push(enriched);
        });
      });
    });
    return index;
  };

  const hasSchema = (candidate) => Boolean(
    candidate?.schema_image_filename &&
    candidate?.highlight?.enabled === true &&
    Number.isFinite(Number(candidate.highlight.x_pct)) &&
    Number.isFinite(Number(candidate.highlight.y_pct))
  );

  const chooseCandidate = (index, handle, preferredModel) => {
    const candidates = (index.get(String(handle || '')) || []).filter(hasSchema);
    if (!candidates.length) return null;
    if (preferredModel) {
      const preferred = candidates.find((candidate) => candidate._modelHandle === preferredModel);
      if (preferred) return preferred;
    }
    return candidates[0];
  };

  const positionCrop = (frame, img, candidate) => {
    const xPct = Number(candidate?.highlight?.x_pct);
    const yPct = Number(candidate?.highlight?.y_pct);
    const marker = frame.querySelector('[data-k-product-schema-marker], .k-product-schema-marker');

    const place = () => {
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      const cw = frame.clientWidth;
      const ch = frame.clientHeight;
      if (!nw || !nh || !cw || !ch) return;

      const cropSide = Math.min(Math.min(nw, nh), Math.max(600, Math.round(Math.min(nw, nh) * 0.36)));
      const scale = Math.max(cw / cropSide, ch / cropSide);
      const rw = nw * scale;
      const rh = nh * scale;

      let left = (cw / 2) - ((xPct / 100) * rw);
      let top = (ch / 2) - ((yPct / 100) * rh);
      left = Math.min(0, Math.max(cw - rw, left));
      top = Math.min(0, Math.max(ch - rh, top));

      img.style.width = rw + 'px';
      img.style.height = rh + 'px';
      img.style.left = left + 'px';
      img.style.top = top + 'px';
      img.style.visibility = 'visible';

      if (marker) {
        marker.hidden = false;
        marker.style.left = (left + ((xPct / 100) * rw)) + 'px';
        marker.style.top = (top + ((yPct / 100) * rh)) + 'px';
      }
    };

    img.style.visibility = 'hidden';
    img.addEventListener('load', place);
    window.requestAnimationFrame(place);
    window.setTimeout(place, 100);
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(place);
      observer.observe(frame);
    }
  };

  const positionContainedMarker = (frame, img, candidate) => {
    const marker = frame.querySelector('[data-k-product-schema-marker], .k-product-schema-marker');
    const xPct = Number(candidate?.highlight?.x_pct);
    const yPct = Number(candidate?.highlight?.y_pct);

    const place = () => {
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      const cw = frame.clientWidth;
      const ch = frame.clientHeight;
      if (!nw || !nh || !cw || !ch) return;

      const scale = Math.min(cw / nw, ch / nh);
      const rw = nw * scale;
      const rh = nh * scale;
      const left = (cw - rw) / 2;
      const top = (ch - rh) / 2;
      const markerLeft = left + ((xPct / 100) * rw);
      const markerTop = top + ((yPct / 100) * rh);

      if (marker) {
        marker.hidden = false;
        marker.style.left = markerLeft + 'px';
        marker.style.top = markerTop + 'px';
      }

      frame.dataset.kMarkerLeft = String(markerLeft);
      frame.dataset.kMarkerTop = String(markerTop);
    };

    img.addEventListener('load', place);
    window.requestAnimationFrame(place);
    window.setTimeout(place, 100);
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(place);
      observer.observe(frame);
    }
  };

  const applySchemaCrop = (frame, candidate, title) => {
    const img = frame.querySelector('img') || document.createElement('img');
    img.alt = title + ' - emplacement sur le schéma du fabricant';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.src = resolveAsset(candidate.schema_preview?.src || candidate.schema_image_filename);
    img.width = Number(candidate.schema_preview?.width) || 600;
    img.height = Number(candidate.schema_preview?.height) || 600;
    if (!img.parentNode) frame.prepend(img);

    if (candidate.schema_preview?.src) {
      img.classList.add('is-direct-schema-crop');
      const marker = frame.querySelector('[data-k-product-schema-marker], .k-product-schema-marker');
      if (marker) marker.hidden = true;
      img.style.visibility = 'visible';
    } else {
      img.classList.remove('is-direct-schema-crop');
      positionCrop(frame, img, candidate);
    }
  };

  const applyFullSchema = (frame, candidate, title) => {
    const img = frame.querySelector('img') || document.createElement('img');
    img.alt = title + ' - schéma du fabricant avec repère ' + (candidate.repere || '');
    img.decoding = 'async';
    img.src = resolveAsset(candidate.schema_image_filename);
    if (!img.parentNode) frame.prepend(img);
    positionContainedMarker(frame, img, candidate);
  };

  const schemaHref = (candidate) => {
    if (!candidate?.schema_handle) return '#';
    const url = new URL('/collections/' + candidate.schema_handle, window.location.origin);
    const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url.pathname + url.search;
  };

  const hydrateCards = (index) => {
    document.querySelectorAll('[data-k-product-visual-card]').forEach((card) => {
      if (card.dataset.kVisualReady === 'true') return;
      const candidate = chooseCandidate(index, card.dataset.productHandle || '', '');
      if (!candidate) return;

      card.dataset.kVisualReady = 'true';
      card.classList.remove('k-card__media--placeholder');
      card.classList.add('k-card__media--schema-fallback');
      card.innerHTML = '';

      const frame = document.createElement('span');
      frame.className = 'k-product-schema-crop k-product-schema-crop--card';
      const img = document.createElement('img');
      const marker = document.createElement('span');
      marker.className = 'k-product-schema-marker';
      marker.setAttribute('aria-hidden', 'true');
      frame.append(img, marker);
      card.append(frame);
      applySchemaCrop(frame, candidate, card.dataset.productTitle || 'Pièce détachée');
    });
  };

  const setupZoom = (root, candidate, title) => {
    const dialog = root.querySelector('[data-k-schema-dialog]');
    const openButton = root.querySelector('[data-k-schema-zoom-open]');
    const closeButton = root.querySelector('[data-k-schema-zoom-close]');
    const zoomIn = root.querySelector('[data-k-schema-zoom-in]');
    const zoomOut = root.querySelector('[data-k-schema-zoom-out]');
    const resetButton = root.querySelector('[data-k-schema-zoom-reset]');
    const viewport = root.querySelector('[data-k-schema-zoom-viewport]');
    const zoomFrame = root.querySelector('[data-k-product-schema-frame="zoom"]');
    if (!dialog || !openButton || !zoomFrame || !viewport) return;

    applyFullSchema(zoomFrame, candidate, title);
    const zoomImage = zoomFrame.querySelector('img');
    if (zoomImage) {
      zoomImage.draggable = false;
      zoomImage.addEventListener('dragstart', (event) => event.preventDefault());
    }

    let scale = 1;
    let dragging = false;
    let suppressNextClick = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let dragStartLeft = 0;
    let dragStartTop = 0;
    let pinchStartDistance = 0;
    let pinchStartScale = 1;
    const pointers = new Map();

    const renderScale = () => {
      zoomFrame.style.width = (scale * 100) + '%';
      zoomFrame.style.height = (scale * 100) + '%';
      viewport.classList.toggle('is-pannable', scale > 1);
      if (resetButton) resetButton.textContent = Math.round(scale * 100) + '%';
    };

    const setScale = (next, focusX = null, focusY = null) => {
      const oldWidth = Math.max(1, zoomFrame.offsetWidth);
      const oldHeight = Math.max(1, zoomFrame.offsetHeight);
      const rect = viewport.getBoundingClientRect();
      const localX = focusX === null ? rect.width / 2 : focusX - rect.left;
      const localY = focusY === null ? rect.height / 2 : focusY - rect.top;
      const focusContentX = viewport.scrollLeft + localX;
      const focusContentY = viewport.scrollTop + localY;
      const ratioX = focusContentX / oldWidth;
      const ratioY = focusContentY / oldHeight;

      scale = Math.max(1, Math.min(4, next));
      renderScale();

      window.requestAnimationFrame(() => {
        if (scale <= 1) {
          viewport.scrollTo({ left: 0, top: 0 });
          return;
        }
        const newWidth = Math.max(1, zoomFrame.offsetWidth);
        const newHeight = Math.max(1, zoomFrame.offsetHeight);
        viewport.scrollLeft = Math.max(0, (ratioX * newWidth) - localX);
        viewport.scrollTop = Math.max(0, (ratioY * newHeight) - localY);
      });
    };

    openButton.addEventListener('click', () => {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      scale = 1;
      renderScale();
      viewport.scrollTo({ left: 0, top: 0 });
    });

    closeButton?.addEventListener('click', () => dialog.close());
    zoomIn?.addEventListener('click', () => setScale(scale + 0.5));
    zoomOut?.addEventListener('click', () => setScale(scale - 0.5));
    resetButton?.addEventListener('click', () => setScale(1));

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    viewport.addEventListener('wheel', (event) => {
      event.preventDefault();
      setScale(scale + (event.deltaY < 0 ? 0.25 : -0.25), event.clientX, event.clientY);
    }, { passive: false });

    viewport.addEventListener('click', (event) => {
      if (suppressNextClick) {
        suppressNextClick = false;
        return;
      }
      if (scale <= 1.05) setScale(2.5, event.clientX, event.clientY);
      else setScale(1);
    });

    viewport.addEventListener('pointerdown', (event) => {
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      viewport.setPointerCapture?.(event.pointerId);

      if (pointers.size === 2) {
        const points = [...pointers.values()];
        pinchStartDistance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        pinchStartScale = scale;
        dragging = false;
        viewport.classList.remove('is-dragging');
        return;
      }

      if (scale <= 1) return;
      dragging = true;
      dragStartX = event.clientX;
      dragStartY = event.clientY;
      dragStartLeft = viewport.scrollLeft;
      dragStartTop = viewport.scrollTop;
      viewport.classList.add('is-dragging');
    });

    viewport.addEventListener('pointermove', (event) => {
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (pointers.size === 2) {
        const points = [...pointers.values()];
        const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        if (pinchStartDistance > 0) {
          const centerX = (points[0].x + points[1].x) / 2;
          const centerY = (points[0].y + points[1].y) / 2;
          setScale(pinchStartScale * (distance / pinchStartDistance), centerX, centerY);
        }
        suppressNextClick = true;
        return;
      }

      if (!dragging || scale <= 1) return;
      const dx = event.clientX - dragStartX;
      const dy = event.clientY - dragStartY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) suppressNextClick = true;
      viewport.scrollLeft = dragStartLeft - dx;
      viewport.scrollTop = dragStartTop - dy;
    });

    const endPointer = (event) => {
      pointers.delete(event.pointerId);
      viewport.releasePointerCapture?.(event.pointerId);
      if (pointers.size < 2) {
        pinchStartDistance = 0;
        pinchStartScale = scale;
      }
      if (pointers.size === 0) {
        dragging = false;
        viewport.classList.remove('is-dragging');
      }
    };

    viewport.addEventListener('pointerup', endPointer);
    viewport.addEventListener('pointercancel', endPointer);

    dialog.addEventListener('close', () => {
      pointers.clear();
      dragging = false;
      suppressNextClick = false;
      viewport.classList.remove('is-dragging');
      scale = 1;
      renderScale();
      viewport.scrollTo({ left: 0, top: 0 });
    });
  };
  // Candidat calcule COTE SERVEUR (k-product-schema-image, mode « candidat ») :
  // la fiche affiche sa vue eclatee sans telecharger finder-data.json (19/10).
  const ssrCandidate = (root) => {
    const node = root.querySelector('script[data-k-pdp-schema-candidate]');
    if (!node) return null;
    try {
      const candidate = JSON.parse(node.textContent || 'null');
      return hasSchema(candidate) ? candidate : null;
    } catch (_error) {
      return null;
    }
  };

  const hydratePdp = (index) => {
    document.querySelectorAll('[data-k-pdp-visuals]').forEach((root) => {
      if (root.dataset.kVisualReady === 'true') return;
      const candidate = ssrCandidate(root)
        || (index ? chooseCandidate(index, root.dataset.productHandle || '', root.dataset.modelHandle || '') : null);
      if (!candidate) return;

      const schemaSlide = root.querySelector('[data-k-gallery-slide="schema"]');
      const mainFrame = root.querySelector('[data-k-product-schema-frame="main"]');
      const thumbFrame = root.querySelector('[data-k-product-schema-frame="thumb"]');
      const schemaThumb = root.querySelector('[data-k-gallery-thumb="schema"]');
      const photoThumb = root.querySelector('[data-k-gallery-thumb="photo"]');
      const thumbs = root.querySelector('[data-k-gallery-thumbs]');
      const placeholder = root.querySelector('[data-k-pdp-visual-placeholder]');
      const meta = root.querySelector('[data-k-pdp-schema-meta]');
      const metaText = root.querySelector('[data-k-pdp-schema-meta-text]');
      const schemaPageLink = root.querySelector('[data-k-pdp-schema-page-link]');
      if (!schemaSlide || !mainFrame || !thumbFrame || !schemaThumb) return;

      root.dataset.kVisualReady = 'true';
      const title = document.querySelector('.k-pdp-spare-v5__title')?.textContent?.trim() || 'Pièce détachée';
      applyFullSchema(mainFrame, candidate, title);
      applyFullSchema(thumbFrame, candidate, title);
      setupZoom(root, candidate, title);

      const schemaUrl = schemaHref(candidate);
      if (schemaPageLink) schemaPageLink.href = schemaUrl;
      if (metaText) {
        metaText.textContent = [
          candidate._modelLabel,
          candidate.repere ? 'repère ' + candidate.repere : '',
        ].filter(Boolean).join(' - ');
      }

      const activate = (type) => {
        root.querySelectorAll('[data-k-gallery-slide]').forEach((slide) => {
          const active = slide.dataset.kGallerySlide === type;
          slide.hidden = !active;
          slide.classList.toggle('is-active', active);
        });
        root.querySelectorAll('[data-k-gallery-thumb]').forEach((button) => {
          const active = button.dataset.kGalleryThumb === type;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        if (meta) meta.hidden = type !== 'schema';
      };

      const hasRealPhoto = root.dataset.hasRealPhoto === 'true';
      schemaThumb.hidden = false;

      if (hasRealPhoto && photoThumb) {
        if (thumbs) thumbs.hidden = false;
        photoThumb.addEventListener('click', () => activate('photo'));
        schemaThumb.addEventListener('click', () => activate('schema'));
        activate('photo');
      } else {
        if (placeholder) placeholder.hidden = true;
        if (thumbs) thumbs.hidden = true;
        activate('schema');
      }
    });
  };

  const chargerFinder = async () => {
    const data = await waitForFinder();
    if (!data) return;
    const index = candidateIndex(data);
    hydrateCards(index);
    hydratePdp(index);
  };

  const init = () => {
    // 1. La fiche d'abord, avec son candidat serveur : aucun telechargement.
    hydratePdp(null);
    const pdpEnAttente = Array.from(document.querySelectorAll('[data-k-pdp-visuals]'))
      .some((root) => root.dataset.kVisualReady !== 'true');
    const cartes = Array.from(document.querySelectorAll('[data-k-product-visual-card]'));
    // 2. Le Finder (1,4 Mo) seulement s'il sert : fiche sans candidat serveur, ou
    //    cartes produit, et pour celles-ci quand elles approchent de l'ecran.
    // Une carte deja a l'ecran (ou presque) charge tout de suite : on n'attend pas
    // le premier rappel de l'observateur (retarde dans un onglet en arriere-plan,
    // constate sur la recherche le 19/10).
    const presDeLEcran = cartes.some((carte) => carte.getBoundingClientRect().top < window.innerHeight + 600);
    if (pdpEnAttente || presDeLEcran || !cartes.length || !('IntersectionObserver' in window)) {
      if (pdpEnAttente || cartes.length) chargerFinder();
      return;
    }
    const observateur = new IntersectionObserver((entrees) => {
      if (entrees.some((entree) => entree.isIntersecting)) {
        observateur.disconnect();
        chargerFinder();
      }
    }, { rootMargin: '600px 0px' });
    cartes.forEach((carte) => observateur.observe(carte));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();